import { useDispatch, useSelector } from 'react-redux';

import {
    setLocationsParamsConnectorStatus,
    setLocationsParamsSearch,
    setLocationsParamsIsFree,
    setLocationsParamsPower,
    setLocationsParamsCanadianMadeOnly,
    setLocationsParamsConnectorTypes,
} from '../store/locations/locations-list.slice';
import { useGetLocationByIdQuery, useGetLocationsQuery } from '../api/locations/locations.api';
import { IFilterLocations, ILocationResponse, ILocationsResponse } from '../api/locations/types';
import { EStationStatus } from '../api/station/types';
import { selectGeoLocation } from '../store/geo-location/geo-location.selector';
import { useMemo } from 'react';

type BaseLocationsHook = {
    setLocationsFilterSearch: (value: string) => Promise<void>;
    setLocationsFilterConnectorStatus: (value: EStationStatus) => Promise<void>;
    setLocationsFilterIsFree: (value: boolean) => Promise<void>;
    setLocationsFilterPower: (value: { maxPower: number; minPower: number }) => Promise<void>;
    setLocationsFilterCanadianMadeOnly: (value: boolean) => Promise<void>;
    setLocationsFilterConnectorTypes: (value: string[]) => Promise<void>;
    locations: ILocationsResponse | undefined;
    isFetching: boolean;
    isLoading: boolean;
};

type WithLocation = {
    location: ILocationResponse | undefined;
};

type LocationsHook<T extends string | undefined> = BaseLocationsHook &
    (T extends string ? WithLocation : Record<string, never>);

export const useLocations = <T extends string | undefined>(locationId?: T): LocationsHook<T> => {
    const { data: location } = useGetLocationByIdQuery(locationId ?? '', {
        skip: !locationId,
    });

    const { entry: geoLocation } = useSelector(selectGeoLocation);
    const locationsListParams = useSelector((state: any) => state.locationsListParams);

    const filter = useMemo(() => {
        const filteredParams = Object.fromEntries(
            Object.entries(locationsListParams).filter(([_, value]) => value !== undefined)
        );

        const { isFree, isPaid, canadianMadeOnly, connectorTypes, ...restParams } = filteredParams;

        return {
            lng: geoLocation.lng,
            lat: geoLocation.lat,
            reload: true,
            cursor: 0,
            isFree: isFree ? true : undefined,
            isPaid: isPaid ? true : undefined,
            canadianMadeOnly: canadianMadeOnly ? true : undefined,
            ...((connectorTypes as Array<string>).length > 0 ? { connectorTypes } : {}),
            ...restParams,
        };
    }, [geoLocation, locationsListParams]);

    const {
        data: locations,
        isFetching,
        isLoading,
    } = useGetLocationsQuery(filter as IFilterLocations);

    const dispatch = useDispatch();

    const setLocationsFilterSearch = async (value: string): Promise<void> => {
        dispatch(setLocationsParamsSearch(value));
    };

    const setLocationsFilterConnectorStatus = async (value: EStationStatus): Promise<void> => {
        dispatch(setLocationsParamsConnectorStatus(value));
    };

    const setLocationsFilterIsFree = async (value: boolean): Promise<void> => {
        dispatch(setLocationsParamsIsFree(value));
    };

    const setLocationsFilterPower = async (value: {
        maxPower: number;
        minPower: number;
    }): Promise<void> => {
        dispatch(setLocationsParamsPower(value));
    };

    const setLocationsFilterCanadianMadeOnly = async (value: boolean): Promise<void> => {
        dispatch(setLocationsParamsCanadianMadeOnly(value));
    };

    const setLocationsFilterConnectorTypes = async (value: string[]): Promise<void> => {
        dispatch(setLocationsParamsConnectorTypes(value));
    };

    return {
        setLocationsFilterSearch,
        setLocationsFilterConnectorStatus,
        setLocationsFilterIsFree,
        setLocationsFilterPower,
        setLocationsFilterCanadianMadeOnly,
        setLocationsFilterConnectorTypes,
        isFetching,
        isLoading,
        locations,
        ...((locationId ? { location } : {}) as T extends string
            ? WithLocation
            : Record<string, never>),
    };
};
